type OptionType = {
	label: string;
	value: string;
};

type SelectOptionsType = {
	label: string;
	value: string;
	group?: string;
};

type OptGroupType = {
	label: string;
	options: SelectOptionsType[];
};

type SelectType = {
	label?: string;
	name: string;
	onChange: (value: string) => void;
	options: SelectOptionsType[];
	required?: boolean;
	testId?: string;
	value: string;
};

function Option({ label, value }: OptionType) {
	return <option value={value}>{label}</option>;
}

function OptGroup({ label, options }: OptGroupType) {
	return (
		<optgroup label={label}>
			{options.map(({ label, value }) => (
				<Option key={value} label={label} value={value} />
			))}
		</optgroup>
	);
}

export default function Select({ label = '', name, onChange, options, required = false, testId = '', value }: SelectType) {
	const selectAttributes = {
		id: name,
		name,
		onChange: (e: React.ChangeEvent<HTMLSelectElement>) => onChange(e.target.value),
		value,
		...(required && { required: true }),
		...(testId && { 'data-testid': testId }),
	};

	// Separate ungrouped and grouped options
	const ungroupedOptions = options.filter((option) => !option.group);
	const groupedOptions = options.filter((option) => option.group);

	// Group the grouped options by their group property
	const optGroups = groupedOptions.reduce(
		(acc, option) => {
			const groupName = option.group!;
			if (!acc[groupName]) {
				acc[groupName] = [];
			}
			acc[groupName].push(option);
			return acc;
		},
		{} as Record<string, SelectOptionsType[]>,
	);

	return (
		<div className="form-field" data-field-name={name} data-field-type="select">
			{label && <label htmlFor={name}>{label}</label>}
			<select {...selectAttributes}>
				{/* Render ungrouped options first */}
				{ungroupedOptions.map(({ label, value }) => (
					<Option key={value} label={label} value={value} />
				))}
				{/* Then render grouped options */}
				{Object.entries(optGroups).map(([groupName, groupOptions]) => (
					<OptGroup key={groupName} label={groupName} options={groupOptions} />
				))}
			</select>
		</div>
	);
}
